import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { ArrowLeft, Loader2, Save, Trash2, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { useCategory, useUpdateCategory, useDeleteCategory, useBiometrics, useBiometricCategories, useCreateBiometricCategory, useDeleteBiometricCategory } from "@/hooks/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function CategoryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: category, isLoading } = useCategory(id!);
  const { data: biometrics } = useBiometrics();
  const { data: biometricCategories } = useBiometricCategories();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const createBiometricCategory = useCreateBiometricCategory();
  const deleteBiometricCategory = useDeleteBiometricCategory();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#6366f1");
  const [selectedBiometricId, setSelectedBiometricId] = useState("");

  useEffect(() => {
    if (category) {
      setName(category.name);
      setDescription(category.description ?? "");
      setColor(category.color ?? "#6366f1");
    }
  }, [category]);

  const handleAddBiometric = async () => {
    if (!id || !selectedBiometricId) return;
    await createBiometricCategory.mutateAsync({
      biometricId: selectedBiometricId,
      categoryId: id,
    });
    setSelectedBiometricId("");
    toast.success("Biomarkør lagt til");
  };

  const handleRemoveBiometric = async (bcId: string) => {
    await deleteBiometricCategory.mutateAsync(bcId);
    toast.success("Biomarkør fjernet");
  };

  const handleSave = async () => {
    if (!id) return;
    await updateCategory.mutateAsync({ id, data: { name, description, color } });
    toast.success("Kategori lagret");
  };

  const handleDelete = async () => {
    if (!id) return;
    await deleteCategory.mutateAsync(id);
    toast.success("Kategori slettet");
    navigate("/categories");
  };

  const biometricIdsInCategory = new Set(
    (biometricCategories ?? []).filter((bc) => bc.categoryId === id).map((bc) => bc.biometricId)
  );
  const categoryBiometrics = biometrics?.filter(
    (b) => biometricIdsInCategory.has(b.id)
  );

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" /></div>;
  }

  if (!category) {
    return (
      <div className="text-center py-12">
        <p className="text-admin-text-subtle mb-4">Kategori ikke funnet</p>
        <Button variant="outline" onClick={() => navigate("/categories")}>
          Tilbake til listen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/categories")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
          <h2 className="text-xl font-semibold">{category.name}</h2>
        </div>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-admin-text-subtle mb-1">Navn</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-admin-text-subtle mb-1">Farge</label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-9 w-12 rounded border border-admin-card-border bg-admin-card cursor-pointer"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-admin-text-subtle mb-1">Beskrivelse</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[160px]"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <h3 className="text-sm font-medium text-admin-text-subtle mb-3">Biomarkorer i denne kategorien</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {categoryBiometrics?.map((b) => {
              const bc = biometricCategories?.find((bc) => bc.biometricId === b.id && bc.categoryId === id);
              return (
                <Badge key={b.id} variant="secondary" className="flex items-center gap-1">
                  {b.name}
                  {bc && (
                    <button onClick={() => handleRemoveBiometric(bc.id)} className="hover:text-admin-negative">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              );
            })}
          </div>
          <div className="flex gap-2">
            <select
              value={selectedBiometricId}
              onChange={(e) => setSelectedBiometricId(e.target.value)}
              className="h-9 w-[200px] rounded-md border border-admin-card-border bg-admin-card px-3 py-1 text-sm text-admin-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-admin-primary-focus cursor-pointer"
            >
              <option value="">Velg biomarkør</option>
              {biometrics
                ?.filter((b) => !categoryBiometrics?.find((cb) => cb.id === b.id))
                .map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
            </select>
            <Button onClick={handleAddBiometric} disabled={!selectedBiometricId || createBiometricCategory.isPending} size="sm">
              <Plus className="h-4 w-4" />
              Legg til
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button onClick={handleSave} disabled={updateCategory.isPending}>
          <Save className="h-4 w-4" />
          Lagre
        </Button>
        <Button variant="destructive" onClick={handleDelete} disabled={deleteCategory.isPending}>
          <Trash2 className="h-4 w-4" />
          Slett
        </Button>
      </div>
    </div>
  );
}
